import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ArrowBack, Email, CheckCircle, Visibility, VisibilityOff, Check, Close } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import EvolunicLogo from '../components/common/EvolunicLogo';

function ForgotPasswordPage() {
  const { forgotPassword, resetPassword, isCognitoMode } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset form fields (Cognito code + new password)
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const passwordRequirements = [
    { label: 'At least 12 characters', test: (p) => p.length >= 12 },
    { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
    { label: 'One number', test: (p) => /[0-9]/.test(p) },
    { label: 'One special character (!@#$%^&*)', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
  ];

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setCodeSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const failedRequirements = passwordRequirements.filter((req) => !req.test(password));
    if (failedRequirements.length > 0) {
      setError('Password does not meet all requirements');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(email, code, password);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{ p: 4, maxWidth: 420, width: '100%', borderRadius: 3, textAlign: 'center' }}
        >
          <Box
            sx={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 3,
            }}
          >
            <CheckCircle sx={{ color: 'white', fontSize: 36 }} />
          </Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Password reset successful
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your password has been reset. You can now log in with your new password.
          </Typography>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none', fontSize: '1rem', fontWeight: 600,
            }}
          >
            Go to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  // Code sent - show verification code + new password form
  if (codeSent) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
        }}
      >
        <Paper elevation={8} sx={{ p: 4, maxWidth: 480, width: '100%', borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 48, height: 48, borderRadius: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5,
              }}
            >
              <EvolunicLogo size={28} color="white" />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}
            >
              Evolunic
            </Typography>
          </Box>

          <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
            Reset your password
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            We sent a verification code to <strong>{email}</strong>. Enter it below with your new password.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleResetPassword}>
            <TextField
              fullWidth
              label="Verification Code"
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(''); }}
              margin="normal"
              required
              autoFocus
              placeholder="Enter the 6-digit code"
              inputProps={{ maxLength: 6 }}
            />

            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              margin="normal"
              required
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {password && (
              <List dense sx={{ mt: 1, mb: 1 }}>
                {passwordRequirements.map((req, index) => {
                  const passed = req.test(password);
                  return (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {passed ? (
                          <Check sx={{ color: 'success.main', fontSize: 18 }} />
                        ) : (
                          <Close sx={{ color: 'error.main', fontSize: 18 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={req.label}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passed ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              margin="normal"
              required
              autoComplete="new-password"
              error={confirmPassword && password !== confirmPassword}
              helperText={
                confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ''
              }
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3, py: 1.5,
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                textTransform: 'none', fontSize: '1rem', fontWeight: 600,
                '&:hover': { background: 'linear-gradient(90deg, #5a6fd6 0%, #6a4190 100%)' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
          </form>

          <Typography variant="body2" align="center" sx={{ mt: 3 }}>
            <Link
              component={RouterLink}
              to="/login"
              sx={{ color: '#667eea', textDecoration: 'none' }}
            >
              <ArrowBack fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              Back to login
            </Link>
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Initial screen - enter email
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
      }}
    >
      <Paper elevation={8} sx={{ p: 4, maxWidth: 420, width: '100%', borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 48, height: 48, borderRadius: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5,
            }}
          >
            <EvolunicLogo size={28} color="white" />
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}
          >
            Evolunic
          </Typography>
        </Box>

        <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
          Forgot password?
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Enter your email address and we'll send you a verification code to reset your password.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSendCode}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            margin="normal"
            required
            autoComplete="email"
            autoFocus
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 3, py: 1.5,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none', fontSize: '1rem', fontWeight: 600,
              '&:hover': { background: 'linear-gradient(90deg, #5a6fd6 0%, #6a4190 100%)' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Verification Code'}
          </Button>
        </form>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Link
            component={RouterLink}
            to="/login"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#667eea', textDecoration: 'none' }}
          >
            <ArrowBack fontSize="small" />
            Back to login
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}

export default ForgotPasswordPage;
